/**
 * Grand Parkway WebMap - Main Application
 *
 * This file initializes the Mapbox GL map and manages layer interactions.
 */

import mapboxgl from 'mapbox-gl';
import '../styles/main.css';
import { MAPBOX_CONFIG, LAYERS, LAYER_GROUPS, SUBDIVISION_LAYER_GROUP, POPUP_CONFIG, PROPERTY_LABELS, ATTRIBUTION } from './config.js';
import { createPopupContent } from './popups.js';
import { initializeLayerControl } from './layerControl.js';
import { addSubdivisionLayers } from './subdivisionLayers.js';
import { addRouteUpdate } from './routeUpdate.js';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_CONFIG.accessToken;

/**
 * Initialize the map
 */
function initializeMap() {
  // Create map instance
  const map = new mapboxgl.Map({
    container: 'map',
    style: MAPBOX_CONFIG.style,
    center: MAPBOX_CONFIG.center,
    zoom: MAPBOX_CONFIG.zoom,
    minZoom: MAPBOX_CONFIG.minZoom,
    maxZoom: MAPBOX_CONFIG.maxZoom,
    maxBounds: MAPBOX_CONFIG.maxBounds
  });

  // Add navigation controls (zoom, rotate)
  map.addControl(new mapboxgl.NavigationControl(), 'top-left');

  // Add scale control
  map.addControl(new mapboxgl.ScaleControl({
    maxWidth: 100,
    unit: 'imperial'
  }), 'bottom-left');

  // Wait for map to load before adding layers
  map.on('load', async () => {
    console.log('Map loaded successfully');

    // Add all parcel layers (GeoJSON - preserves 100% of data)
    await addParcelLayers(map);

    // Add subdivision/development layers (local GeoJSON)
    await addSubdivisionLayers(map);

    // Add highlighted SH 99 Segment C phase-advance update (drawn on top)
    await addRouteUpdate(map);

    // Initialize layer control panel (includes both parcels and subdivisions)
    initializeLayerControl(map, LAYER_GROUPS, SUBDIVISION_LAYER_GROUP);

    // Fit map to initial bounds
    map.fitBounds(MAPBOX_CONFIG.bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 350 },  // Account for layer control
      duration: 1000
    });

    console.log('All layers added and configured');
  });

  // Error handling
  map.on('error', (e) => {
    console.error('Map error:', e);
  });

  return map;
}

/**
 * Add all parcel layers to the map (loading GeoJSON directly for 100% data retention)
 */
async function addParcelLayers(map) {
  console.log('Loading parcel data (this may take a moment)...');

  for (const layer of LAYERS) {
    try {
      console.log(`Loading ${layer.name}...`);

      // Fetch GeoJSON data
      const response = await fetch(layer.geojsonFile);
      if (!response.ok) {
        throw new Error(`Failed to load ${layer.geojsonFile}: ${response.statusText}`);
      }
      const geojsonData = await response.json();

      console.log(`  ✓ Loaded ${geojsonData.features?.length || 0} features`);

      // Add source (GeoJSON)
      map.addSource(layer.id, {
        type: 'geojson',
        data: geojsonData
      });

      // Add fill layer
      map.addLayer({
        id: `${layer.id}-fill`,
        type: 'fill',
        source: layer.id,
        layout: {
          visibility: layer.visible ? 'visible' : 'none'
        },
        paint: {
          'fill-color': layer.style.fillColor,
          'fill-opacity': layer.style.fillOpacity
        }
      });

      // Add line layer (parcel borders)
      map.addLayer({
        id: `${layer.id}-line`,
        type: 'line',
        source: layer.id,
        layout: {
          visibility: layer.visible ? 'visible' : 'none'
        },
        paint: {
          'line-color': layer.style.lineColor,
          'line-width': layer.style.lineWidth
        }
      });

      // Add click handler for popups
      map.on('click', `${layer.id}-fill`, (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const coordinates = e.lngLat;

          // Create popup content
          const content = createPopupContent(feature.properties, layer.county);

          // Create and display popup
          new mapboxgl.Popup(POPUP_CONFIG)
            .setLngLat(coordinates)
            .setHTML(content)
            .addTo(map);
        }
      });

      // Change cursor on hover
      map.on('mouseenter', `${layer.id}-fill`, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', `${layer.id}-fill`, () => {
        map.getCanvas().style.cursor = '';
      });

      console.log(`  ✓ Added layer: ${layer.name}`);

    } catch (error) {
      console.error(`Error loading ${layer.name}:`, error);
    }
  }

  console.log('All parcel layers loaded successfully');
}

/**
 * Toggle layer visibility
 * @param {mapboxgl.Map} map - The map instance
 * @param {string} layerId - The layer ID to toggle
 * @param {boolean} visible - Whether the layer should be visible
 */
export function toggleLayerVisibility(map, layerId, visible) {
  const visibility = visible ? 'visible' : 'none';

  // Toggle both fill and line layers
  if (map.getLayer(`${layerId}-fill`)) {
    map.setLayoutProperty(`${layerId}-fill`, 'visibility', visibility);
  }
  if (map.getLayer(`${layerId}-line`)) {
    map.setLayoutProperty(`${layerId}-line`, 'visibility', visibility);
  }

  console.log(`Layer ${layerId} visibility: ${visibility}`);
}

// Initialize the application when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('Initializing Grand Parkway WebMap...');

  // Check if access token is configured
  if (MAPBOX_CONFIG.accessToken === 'pk.YOUR_TOKEN_HERE') {
    console.error('⚠️  Mapbox access token not configured!');
    console.error('Please set VITE_MAPBOX_ACCESS_TOKEN in your .env.local file');
    document.getElementById('map').innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; background: #f5f5f5; font-family: Arial, sans-serif;">
        <div style="text-align: center; padding: 20px; background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          <h2 style="color: #e31a1c; margin-bottom: 10px;">⚠️ Configuration Required</h2>
          <p style="margin-bottom: 10px;">Mapbox access token not configured.</p>
          <p style="font-size: 14px; color: #666;">
            Please create a <code>.env.local</code> file with your Mapbox token.
            <br><br>
            See <code>MAPBOX_UPLOAD_GUIDE.md</code> for instructions.
          </p>
        </div>
      </div>
    `;
    return;
  }

  // Initialize the map
  const map = initializeMap();

  // Make map available globally for debugging
  window.map = map;
});

// Export for use in other modules
export { initializeMap };
